import { cn } from "@/lib/utils";
import { PanelLeft } from "lucide-react";
import type { ReactNode } from "react";
import { IconButton } from "./icon-button";
import { InvectorLogo } from "./logo";

// 높이 61px = 36px 버튼 + 상하 12px + 하단 선 1px. 아래 클래스와 숫자는 같은 값이어야 한다.
const MAIN_HEADER_HEIGHT_CLASS = "h-[61px]";
/** AppPageShell 이 `--main-header-height` 로 내려보내 fixed 사이드바가 그만큼 내려앉는다 */
export const MAIN_HEADER_HEIGHT_PX = 61;

export interface MainHeaderProps {
  /** 페이지 이름. 로고 오른쪽에 온다 */
  title: string;
  /**
   * 사이드바 토글. 상태는 페이지가 소유하고 여기서는 받기만 한다
   * (Figma mainHeader sideBar=Default/fold). 사이드바가 없는 페이지는 생략.
   * onHoverChange: 토글 hover 진입/이탈 — 접힌 사이드바를 hover 로 미리 띄우는 페이지가 쓴다 (비활성이면 부르지 않음).
   */
  sidebar?: {
    open: boolean;
    onToggle: () => void;
    disabled?: boolean;
    onHoverChange?: (hovering: boolean) => void;
  };
  /** 로고 클릭. 없으면 로고는 이미지로만 */
  onLogoClick?: () => void;
  /** 페이지명 오른쪽 슬롯 (Figma: headerChip 필터 토글, 라벨 자리) */
  titleAddon?: ReactNode;
  /** 가운데 슬롯 (Figma: View Switch Group 자리) */
  center?: ReactNode;
  /** 오른쪽 슬롯 (Figma: 버튼 묶음 자리) */
  actions?: ReactNode;
  className?: string;
}

/**
 * 앱 공용 상단 바 (Figma mainHeader, 61px). 시각과 슬롯만 담당한다.
 * 라우터·페이지명 연결은 components/app-shell 의 AppMainHeader 가 한다.
 */
export const MainHeader = ({
  title,
  sidebar,
  onLogoClick,
  titleAddon,
  center,
  actions,
  className,
}: MainHeaderProps) => (
  <header
    className={cn(
      MAIN_HEADER_HEIGHT_CLASS,
      "flex shrink-0 items-center gap-2 border-b border-line-normal bg-white py-3 pl-3 pr-4",
      className,
    )}
  >
    <div className="flex min-w-0 flex-1 items-center gap-4">
      <div className="flex shrink-0 items-center gap-2">
        {sidebar && (
          <IconButton
            variant="transparent"
            size={36}
            // 아이콘은 상태와 무관하게 같고, 접힌 동안만 점 배지 (Figma sideBar=fold)
            icon={<PanelLeft />}
            showBadge={!sidebar.open}
            disabled={sidebar.disabled}
            aria-label={sidebar.open ? "사이드바 접기" : "사이드바 펼치기"}
            aria-expanded={sidebar.open}
            onClick={sidebar.onToggle}
            onMouseEnter={
              sidebar.disabled ? undefined : () => sidebar.onHoverChange?.(true)
            }
            onMouseLeave={() => sidebar.onHoverChange?.(false)}
          />
        )}
        {onLogoClick ? (
          <button
            type="button"
            onClick={onLogoClick}
            aria-label="홈으로"
            className="flex items-center"
          >
            <InvectorLogo />
          </button>
        ) : (
          <InvectorLogo />
        )}
      </div>
      <h1 className="label-1-normal-semibold truncate text-label-normal">
        {title}
      </h1>
      {titleAddon && (
        <div className="flex shrink-0 items-center gap-2">{titleAddon}</div>
      )}
    </div>
    {center}
    {/* 좌우 part 가 같은 flex-1 이라 양쪽 내용이 제 몫보다 좁을 때 center 가 정중앙에 온다. 오른쪽에 버튼이 많으면 그만큼 왼쪽으로 밀린다 (Figma 는 항상 정중앙이지만 absolute 로 띄우면 슬롯끼리 겹친다) */}
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      {actions}
    </div>
  </header>
);
